import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { Check, Lock, TrendingDown, TrendingUp } from 'lucide-react';
import { formatCurrency } from '../../common/formatters';

// Converte o `amount` de uma linha da listagem (`GET /transactions/query`,
// número ou string) para o texto formatado ("1.234,56") esperado pelo campo
// Valor — cópia 1:1 do helper que já existia em `TransactionEditForm.js`,
// agora compartilhado com `Form.js` (variante completa de edição).
export function amountFromRowToInput(amount) {
    if (amount == null || amount === '') return '';
    if (typeof amount === 'number' && Number.isFinite(amount)) {
        return formatCurrency(String(Math.round(amount * 100 + Number.EPSILON)));
    }
    return formatCurrency(String(amount));
}

// Lê uma mensagem de erro legível de uma resposta do `httpClient` (que nunca
// rejeita por status HTTP — ver `common/httpclient.js`).
export function readApiErrorMessage(response) {
    const data = response?.data;
    if (typeof data === 'string' && data.trim()) return data.trim();
    if (data && typeof data === 'object' && data.error) return String(data.error);
    return '';
}

// Casca visual do campo "ff-input"/"ff-textarea" (52px/auto, radius 12px,
// fundo surface-2, label flutuante interno) — cópia 1:1 de
// `useStandaloneFieldSx` já usado em `pages/cashregisters/shared.js`/
// `pages/categories/Form.js`/`pages/services/Form.js`/`pages/products/Form.js`.
export function useStandaloneFieldSx() {
    const standaloneInputBoxSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            backgroundColor: 'background.subtle',
            height: '52px',
            transition: 'background-color .15s ease, box-shadow .15s ease',
            '&.MuiInputBase-multiline': {
                height: 'auto',
                minHeight: '96px',
                alignItems: 'flex-start',
                padding: '24px 14px 7px 14px',
            },
        },
        '& .MuiOutlinedInput-input:not(textarea):not(.MuiSelect-select)': {
            padding: '21px 14px 7px 14px',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderWidth: '1.5px',
            borderColor: 'divider',
        },
        '& .MuiOutlinedInput-notchedOutline legend': {
            display: 'none',
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'primary.main',
            borderWidth: '1.5px',
        },
        '& .MuiInputBase-multiline textarea': {
            resize: 'vertical',
        },
    };

    return {
        '& .MuiInputBase-input': { fontSize: '13.5px' },
        '& .MuiInputLabel-root': {
            fontSize: '13.5px',
            color: 'text.disabled',
            transform: 'translate(14px, 17px) scale(1)',
        },
        '& .MuiInputLabel-root.MuiInputLabel-shrink': {
            fontSize: '10px',
            fontWeight: 800,
            color: 'primary.main',
            letterSpacing: '.04em',
            textTransform: 'uppercase',
            transform: 'translate(14px, 8px) scale(1)',
        },
        ...standaloneInputBoxSx,
    };
}

// setting-row (§5.6, mesma cópia de `pages/cashregisters/shared.js`) — ícone +
// título/descrição + switch, usado pelo toggle "Pagar pelo caixa agora".
export function settingRowSx(isLast) {
    return {
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        py: '14px',
        borderBottom: isLast ? 'none' : '1px solid',
        borderColor: 'divider',
    };
}

// Radio-card de Tipo de transação (Receita verde / Despesa vermelho) — mesmo
// padrão `:has(input:checked)` dos `move-card` de `prototipo-reposicao-
// estoque.html` (DESIGN-SYSTEM.md §5.13), reaproveitado verbatim conforme o
// próprio §5.13 recomenda ("reuse verbatim for any future 'pick one of two
// opposite directions' flow").
export function TxTypeCard({ selected, tone, icon: Icon, title, description, onSelect, disabled }) {
    const toneMain = `${tone}.main`;
    const toneBg = `${tone}.bg`;
    return (
        <Box
            role="radio"
            aria-checked={selected}
            aria-disabled={disabled || undefined}
            tabIndex={disabled ? -1 : 0}
            onClick={disabled ? undefined : onSelect}
            onKeyDown={(e) => {
                if (disabled) return;
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelect();
                }
            }}
            sx={{
                position: 'relative',
                border: '1.5px solid',
                borderColor: selected ? toneMain : 'divider',
                borderRadius: '14px',
                p: '14px 16px',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.65 : 1,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.25,
                backgroundColor: selected ? toneBg : 'background.subtle',
                outline: 'none',
                transition: 'border-color .15s, background-color .15s',
                '&:hover': disabled ? {} : { borderColor: selected ? toneMain : 'text.disabled' },
                '&:focus-visible': disabled ? {} : { boxShadow: (t) => `0 0 0 3px ${t.palette.info.bg}` },
            }}
        >
            <Box
                sx={{
                    width: 34,
                    height: 34,
                    borderRadius: '9px',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: selected ? toneMain : 'background.paper',
                    color: selected ? '#fff' : 'text.disabled',
                    border: '1px solid',
                    borderColor: selected ? toneMain : 'divider',
                    transition: 'background-color .15s, color .15s',
                }}
            >
                <Icon size={16} strokeWidth={2} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, mb: '2px' }}>{title}</Typography>
                <Typography sx={{ fontSize: 11, color: 'text.disabled', lineHeight: 1.35 }}>{description}</Typography>
            </Box>
            {selected && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 10,
                        right: 10,
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        backgroundColor: toneMain,
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Check size={10} strokeWidth={3} />
                </Box>
            )}
        </Box>
    );
}

// Badge somente-leitura "Despesa" — variante de edição compacta
// (`TransactionEditForm`), substitui os radio-cards quando o tipo não pode
// ser escolhido (edição rápida de despesa Pendente/Vencido).
export function TxCompactTypeBadge() {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                p: '12px 14px',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '12px',
                backgroundColor: 'error.bg',
                mb: '20px',
            }}
        >
            <Box
                sx={{
                    width: 34,
                    height: 34,
                    borderRadius: '9px',
                    backgroundColor: 'error.main',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                }}
            >
                <TrendingDown size={16} strokeWidth={2} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Despesa</Typography>
                <Typography sx={{ fontSize: '11.5px', color: 'text.secondary' }}>
                    Edição rápida — o tipo não pode ser alterado aqui.
                </Typography>
            </Box>
        </Box>
    );
}

// Nota de trava de tipo (`tx-type-lock-note`) — exibida abaixo dos
// radio-cards quando o tipo não pode mais ser alterado (edição de um
// lançamento já existente).
export function TxTypeLockNote() {
    return (
        <Typography
            sx={{
                fontSize: 11,
                color: 'text.disabled',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                mt: '2px',
                mb: '18px',
            }}
        >
            <Lock size={12} strokeWidth={2} />
            O tipo não pode ser alterado depois que a transação é criada.
        </Typography>
    );
}

// Ícone do header do drawer (`dh-ic`), cor conforme o tipo selecionado —
// mesmo racional de `updateDrawerIcon()` do protótipo.
export function TxDrawerHeaderIcon({ type }) {
    const theme = useTheme();
    const isRevenue = type !== 'despesa';
    return (
        <Box
            sx={{
                width: 38,
                height: 38,
                borderRadius: '11px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                mb: '10px',
                backgroundColor: isRevenue ? theme.palette.success.bg : theme.palette.error.bg,
                color: isRevenue ? theme.palette.success.main : theme.palette.error.main,
                transition: 'background-color .15s, color .15s',
            }}
        >
            {isRevenue ? <TrendingUp size={18} strokeWidth={2} /> : <TrendingDown size={18} strokeWidth={2} />}
        </Box>
    );
}
